import { Component, Event, EventEmitter, h, Prop, State, Watch } from '@stencil/core';
import { i18nConfig, EmailCollection } from '../../../../../interfaces';
import { timePassed } from '../../../../../util';

@Component({
  tag: 'presto-email-overlay-controller',
  styleUrl: 'presto-email-overlay-controller.css',
  shadow: false,
})
export class PrestoEmailOverlayController {
  @Prop() ended: boolean;
  @Prop({ reflect: true }) currentTime: number;
  @Prop({ reflect: true }) duration: number;
  @Prop() direction?: 'rtl';
  @Prop() emailCollection?: EmailCollection;
  @Prop() i18n: i18nConfig;
  @Prop() videoId: number;
  @Prop() presetId: number;
  @Prop() provider: string;

  @State() enabled: boolean;
  @State() show: boolean;
  @State() loading: boolean;
  @State() error: string;
  @State() percentagePassed: number = 0;

  @Event() playVideo: EventEmitter<void>;
  @Event() pauseVideo: EventEmitter<true>;
  @Event() restartVideo: EventEmitter<void>;
  @Event() emailStateChange: EventEmitter<boolean>;

  /**
   * Set email collection in local storage
   * @param status string
   */
  setStorage(status) {
    window.localStorage.setItem('presto.videos.email_collection', JSON.stringify({ [this.videoId]: status }));
  }

  /**
   * Get email collection in local storage
   * @returns status string
   */
  getStorage() {
    return window.localStorage.getItem('presto.videos.email_collection');
  }

  componentWillLoad() {
    this.handleDuration();
  }

  /**
   * Wait for duration to start before checking time
   * @returns void
   */
  @Watch('duration')
  handleDuration() {
    this.enabled = this.getStorage() ? false : this?.emailCollection?.enabled;
    this.handleTimeCheck();
  }

  /**
   * When current time changes, check to see if we should
   * enable the overlay
   * @returns void
   */
  @Watch('currentTime')
  handleTimeCheck() {
    if (!this.enabled) return; // not enabled
    if (this.getStorage()) return; // already skipped
    this.checkTime();
  }

  @Watch('currentTime')
  handleShowChange() {
    if (!this.show) return; // bail if not showing.
    this.pauseVideo.emit(true); // pause video if here.
  }

  /**
   * Set enabled/disabled based on time that has passed
   */
  checkTime() {
    this.show = timePassed({
      current: this.currentTime,
      duration: this.duration,
      showAfter: this?.emailCollection?.percentage || 0,
    });
  }

  /**
   * Fetch updated nonce in case of caching
   * @returns Promise
   */
  async getNonce() {
    return fetch(`${window?.prestoPlayer?.ajaxurl}?action=presto_refresh_progress_nonce`);
  }

  /**
   * Submit email collection
   * @param e Event
   */
  async submit(e) {
    this.loading = true;
    this.error = '';

    // get nonce refresh
    const response = await this.getNonce();
    const { data: nonce } = await response.json();

    // handle submit
    try {
      let response = await fetch(window?.prestoPlayer?.ajaxurl, {
        method: 'post',
        body: new URLSearchParams({
          action: 'presto_player_email_submit',
          nonce,
          preset_id: this.presetId,
          video_id: this.videoId,
          provider: this.provider,
          ...(e?.detail || {}),
        }),
      });
      const { success, data } = await response.json();
      if (success) {
        this.setStorage('collected');
        this.show = false;
        this.playVideo.emit();
      } else {
        throw data;
      }
    } catch (e) {
      const error = e?.[0];
      if (error && typeof error === 'string') {
        this.error = error;
      }
    } finally {
      this.loading = false;
    }
  }

  /**
   * Skip email collection
   */
  skip() {
    this.setStorage('skipped');
    this.show = false;
    this.playVideo.emit();
  }

  /**
   * Maybe render
   * @returns JSX
   */

  @Watch('show')
  handleEmailStateChange(val) {
    this.emailStateChange.emit(val);
  }

  render() {
    if (!this.show) {
      return;
    }

    return (
      <presto-email-overlay-ui
        style={{
          ...(this?.emailCollection?.button_color
            ? {
                '--presto-player-button-color': `${this?.emailCollection?.button_color}`,
              }
            : {}),
          ...(this?.emailCollection?.button_text_color
            ? {
                '--presto-player-button-text': `${this?.emailCollection?.button_text_color}`,
              }
            : {}),
        }}
        direction={this.direction}
        class="email-overlay"
        headline={this?.emailCollection?.headline}
        bottomText={this?.emailCollection?.bottom_text}
        allowSkip={this?.emailCollection?.allow_skip}
        buttonText={this?.emailCollection?.button_text}
        isLoading={this.loading}
        errorMessage={this.error}
        onSubmitForm={e => this.submit(e)}
        onSkip={() => this.skip()}
        i18n={this.i18n}
        provider={this.provider}
      ></presto-email-overlay-ui>
    );
  }
}
